import { JSX } from 'react';
import {
    useGetIdentity, UseGetIdentityResult, Title
} from 'react-admin';
import {
    Box,
    Grid,
    LinearProgress,
    Typography
} from '@mui/material'
import { TokenHistoryGraph } from "./components/TokenHistoryGraph";
import { TokenHistoryTimings } from "./components/TokenHistoryTimings";
import { useAuthenticated } from 'react-admin';
import { usePermissions } from 'react-admin';
import { permissionManager } from "../permissions/manager";
import { EnterpriseRequestsData } from "./components/EnterpriseRequestsData";
import { ContactFormData } from "./components/ContactFormData";
import { ConversationsAwaitingReplyData } from "./components/ConversationsAwaitingReplyData";
import { NokProcessingFailuresData } from "./components/NokProcessingFailuresData";
import { TokenCostSummary } from "./components/TokenCostSummary";
import { TokenCostProvider } from "./components/context/TokenCostContext";
import { DashboardTabs, DashboardTab } from "./components/DashboardTabs";
import { eyebrow } from "./components/styles/dashboard-tokens";

const todayFormat: Intl.DateTimeFormat = new Intl.DateTimeFormat('en-GB', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
});

/**
 * The dashboard shows work waiting on a person, and each card reads a different resource.
 *
 * **A card the signed-in admin cannot reach must not mount at all.** Two of these render a
 * `<List>`, whose controller calls react-admin's `useRequireAccess`, and that does not hide the
 * card: it navigates the **whole application** to `/access-denied`. So a narrow role signing in
 * landed on a refusal page it could not leave, with every one of its own screens working
 * perfectly behind it. Found in the browser on 2026-09-10 with a sales-only role.
 */
const AttentionPanel: () => JSX.Element = (): JSX.Element => {
    const { permissions } = usePermissions();
    const shows = (resource: string): boolean => permissionManager.isAllowed(permissions, resource);

    return (
        <Grid container spacing={ 2 }>
            <Grid size={ 12 }>
                <Grid container spacing={ 2 }>
                    { shows('HQ_Sales::enterprise_requests') &&
                        <Grid size={ { xs: 12, md: 6 } }>
                            <EnterpriseRequestsData />
                        </Grid>
                    }
                    { shows('HQ_Sales::contact_forms') &&
                        <Grid size={ { xs: 12, md: 6 } }>
                            <ContactFormData />
                        </Grid>
                    }
                </Grid>
            </Grid>
            { /* Full width, not a third card in the row above: those two already overflow
                 below ~1600px (HQ-09) and a third would make it worse. */ }
            { shows('HQ_Sales::conversation') &&
                <Grid size={ 12 }>
                    <ConversationsAwaitingReplyData />
                </Grid>
            }
            { /* Full width for the same reason as the card above: six columns will not fit a
                 half-width card without scrolling sideways (HQ-09). Directly under the
                 conversations because it is the same question, work waiting on a person. */ }
            { shows('HQ_Data::nok_processing_failures') &&
                <Grid size={ 12 }>
                    <NokProcessingFailuresData />
                </Grid>
            }
        </Grid>
    );
}

/** Telemetry. What the platform ran today and what it cost. */
const UsagePanel: () => JSX.Element = (): JSX.Element => {
    return (
        // The spend card and the budget card's all-time line are the same report, so it is
        // fetched once here and shared. See TokenCostContext.
        <TokenCostProvider>
        <Grid container spacing={ 2 }>
            { /* Money first. It is the figure somebody opens this tab to find, and the token
                 charts below are the working behind it. */ }
            <Grid size={ 12 }>
                <TokenCostSummary />
            </Grid>
            <Grid size={ 12 }>
                <TokenHistoryGraph />
            </Grid>
            <Grid size={ 12 }>
                <TokenHistoryTimings />
            </Grid>
        </Grid>
        </TokenCostProvider>
    );
}

const DASHBOARD_TABS: DashboardTab[] = [
    { id: 'attention', label: 'Needs attention', content: <AttentionPanel /> },
    { id: 'usage', label: 'Usage & cost', content: <UsagePanel /> },
];

export const DashboardContainer: () => JSX.Element = (): JSX.Element => {
    // useAuthenticated() only *triggers* the auth check and redirect — on its own it
    // does not stop children rendering. Without gating on its result every widget
    // below mounted and fetched while logged out, producing 401s and an error toast
    // over the login page. `authenticated` is true while the check is still pending,
    // so both flags have to be tested.
    const { isPending, authenticated } = useAuthenticated();
    const user: UseGetIdentityResult = useGetIdentity();

    if (isPending || !authenticated) {
        return <LinearProgress sx={ { width: '100%' } } />;
    }

    return (
        // React Admin's content area has no right gutter, so without this the right-hand column
        // sat flush against the window edge while the left had a margin.
        <Box sx={ { pr: { xs: 0, sm: 2 }, pb: 3 } }>
            <Grid container spacing={2}>
                { /* Owned here rather than by whichever child <List> happened to mount last. */ }
                <Title title="Dashboard" />
                <Grid size={ 12 } className="dashboard-welcome" sx={ { pt: 4, pb: 0.5 } }>
                    <Typography component="p" sx={ eyebrow }>
                        { todayFormat.format(new Date()) }
                    </Typography>
                    <Typography variant="h5" component="h1" sx={ { fontWeight: 700, letterSpacing: '-0.01em' } }>
                        { "Welcome back, " + (user?.data?.fullName ?? 'Guest') }
                    </Typography>
                </Grid>
                { /* Six cards at a 405px minimum ran to roughly 2,500px of scroll, with work
                     waiting on a person mixed in among telemetry. Split by the question each
                     card answers rather than by how often it changes. */ }
                <Grid size={ 12 }>
                    <DashboardTabs tabs={ DASHBOARD_TABS } storageKey="hq.dashboard.tab" />
                </Grid>
            </Grid>
        </Box>
    );
}
